import { CitationMatchTable } from 'components/citation-match-table';
import { Link } from 'components/link';
import type { DocumentResponse } from 'types/api-responses';
import { EMPTY_CELL } from 'utils/empty-cell';
import { TargetCitationMatchSection } from './target-citation-match-section';
import style from './citation-tables.module.scss';

// The overview's provision-citation tables: each lists what the document says
// beside what it was matched to.

/**
 * Names an entity's STI type the way the tables label it.
 * @param {string} type - The entity's class name, e.g. "OrganizationEntity"
 * @return {string} The short label, e.g. "Organization"; unknown types pass through
 */
const formatEntityType = (type: string) => {
  switch (type) {
    case 'ImportedEntity':
      return 'Imported';
    case 'IndividualEntity':
      return 'Individual';
    case 'OrganizationEntity':
      return 'Organization';
    default:
      return type;
  }
};

/**
 * The party citations beside the entities each was matched to, with their details.
 * @param {{ document: DocumentResponse }} props - The document whose party citations to list
 * @return {JSX.Element} The party citation table
 */
export function PartyCitations({ document }: { document: DocumentResponse }) {
  const partyCitations =
    document.citations?.filter(
      (citation) => citation.provisionField === 'parties'
    ) || [];

  return (
    <CitationMatchTable
      citations={partyCitations}
      extractedColumnHeader="Party Details From Document"
      extractedColumnTooltip='The raw "as written" Party name in a document before it is structured into an Insights Entity.'
      matchColumnHeader="Entity Match"
      testId="party-citations-table"
      renderMatch={(citation) => {
        if (citation.entities && citation.entities.length > 0) {
          return citation.entities.map((entity) => (
            <div key={entity.id} className={style.entityMatchCell}>
              <Link to={`/entities/${entity.id}`}>
                {entity.name}, {entity.id}
              </Link>
              <div className={style.entityType}>
                Entity Type: {formatEntityType(entity.type)}
              </div>
              {entity.address && (
                <div className={style.entityType}>
                  Address: {entity.address}
                </div>
              )}
              {entity.capacity && (
                <div className={style.entityType}>
                  Capacity: {entity.capacity}
                </div>
              )}
              {entity.interestPercentage && (
                <div className={style.entityType}>
                  Interest: {entity.interestPercentage}
                </div>
              )}
              {entity.emails && entity.emails.length > 0 && (
                <div className={style.entityType}>
                  Email: {entity.emails.join(', ')}
                </div>
              )}
              {entity.phones && entity.phones.length > 0 && (
                <div className={style.entityType}>
                  Phone: {entity.phones.join(', ')}
                </div>
              )}
            </div>
          ));
        }
        return EMPTY_CELL;
      }}
    />
  );
}

/**
 * The regulatory-body citations beside the bodies each was matched to.
 * @param {{ document: DocumentResponse }} props - The document whose regulatory-body citations to list
 * @return {JSX.Element} The regulatory body citation table
 */
export function RegulatoryBodyCitations({
  document,
}: {
  document: DocumentResponse;
}) {
  const regulatoryBodyCitations =
    document.citations?.filter(
      (citation) => citation.provisionField === 'regulatory_body'
    ) || [];

  return (
    <CitationMatchTable
      citations={regulatoryBodyCitations}
      extractedColumnHeader="Regulatory Body Details From Document"
      extractedColumnTooltip="The regulatory body name extracted from the document."
      matchColumnHeader="Regulatory Body Details Match"
      testId="regulatory-body-citations-table"
      renderMatch={(citation) => {
        if (citation.regulatoryBodies && citation.regulatoryBodies.length > 0) {
          return citation.regulatoryBodies.map((body) => (
            <div key={body.id}>
              {body.name}
              {body.abbreviation && ` (${body.abbreviation})`}
            </div>
          ));
        }
        return EMPTY_CELL;
      }}
    />
  );
}

/**
 * A regulatory document's regulated-target citations beside their matched targets.
 * @param {{ document: DocumentResponse }} props - The regulatory document
 * @return {JSX.Element} The regulated target citation section
 */
export function RegulatedTargetCitations({
  document,
}: {
  document: DocumentResponse;
}) {
  return (
    <TargetCitationMatchSection
      document={document}
      documentType="Document::Regulatory"
      header=""
      provisionField="regulated_target"
      extractedColumnHeader="Regulatory Target Details From Document"
      extractedColumnTooltip="Regulated target identifiers extracted from the document."
      matchColumnHeader="Regulatory Target Details Match"
      testId="regulated-target-citations-table"
      renderTargetType={(target) => target.type}
      targetTypeLabel="Target Type"
    />
  );
}

/**
 * A division order's well citations beside the wells each was matched to.
 * @param {{ document: DocumentResponse }} props - The division order document
 * @return {JSX.Element} The well citation table
 */
export function DivisionOrderWellsCitations({
  document,
}: {
  document: DocumentResponse;
}) {
  const wellCitations =
    document.citations?.filter(
      (citation) => citation.provisionField === 'division_order_target'
    ) || [];

  return (
    <CitationMatchTable
      citations={wellCitations}
      extractedColumnHeader="Wells From Document"
      extractedColumnTooltip="Well or property identifiers extracted from the division order."
      matchColumnHeader="Matched Wells"
      testId="division-order-wells-citations-table"
      renderMatch={(citation) => {
        if (citation.regulatedTargets && citation.regulatedTargets.length > 0) {
          return citation.regulatedTargets.map((target) => (
            <div key={target.id}>
              <Link to={target.href}>{target.displayName}</Link>
              {target.uwi && (
                <div className={style.entityType}>UWI: {target.uwi}</div>
              )}
              <div className={style.entityType}>Target Type: {target.type}</div>
            </div>
          ));
        }
        return EMPTY_CELL;
      }}
    />
  );
}
